import React, { createContext, useContext, useState, useEffect } from 'react';
import { CategoryInsightsSettingsContext } from 'contexts/CategoryInsightsSettingsContext';
import { CategoryManagementContext } from 'contexts/CategoryManagementContext';
import { getPaymentTerms } from 'utils/apiRequests';
import {
	defaultColDefination,
	initialGridData,
} from 'CategoryManagement/Explore/DetailedAnalysis/utils';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import { getRequiredCustomFilterObjectFormat } from 'CategoryManagement/utils';

const PaymentTermsContext = createContext();

const daysText = 'spend below';
const columnDefs = [
	{ headerName: 'Category', field: 'category' },
	{
		headerName: 'Vendor Name',
		field: 'vendor',
	},
	{ headerName: 'DPO', field: 'dpo' },
	{
		headerName: 'Total Spend',
		field: 'totalSpend',
	},
];

export const PaymentTermsProvider = ({
	children,
	currencySymbol,
	hideDecimals,
}) => {
	const { isSaving, paymentTerms, loading: isSettingsFetching } = useContext(
		CategoryInsightsSettingsContext
	);
	const {
		currentSelectCategory,
		dateRangeData,
		isFetching,
		isDirect,
		isSourceable,
		taxonomyType,
		customFilters,
		customFiltersValues,
	} = useContext(CategoryManagementContext);
	const [isLoading, setIsLoading] = useState(false);
	const [analysis, setAnalysis] = useState(`X ${daysText} x days`);
	const [gridData, setGridData] = useState(initialGridData);
	const { categoryName } = currentSelectCategory;
	const { dateFrom, dateTo } = dateRangeData;

	useEffect(
		() => {
			if (!isSaving && !isSettingsFetching && !isFetching) {
				setIsLoading(true);
				if (
					categoryName !== '' ||
					categoryName !== null ||
					categoryName !== undefined
				) {
					const payload = {
						categoryName: categoryName,
						dateFrom: dateFrom,
						dateTo: dateTo,
						isSourceable: isSourceable,
						aptaxonomy: isDirect,
						taxonomyType: taxonomyType,
					};
					if (
						Array.isArray(customFiltersValues) &&
						customFiltersValues.length > 0
					) {
						payload.customfilter = getRequiredCustomFilterObjectFormat(
							customFilters,
							customFiltersValues
						);
					}
					getPaymentTerms(payload)
						.then(response => {
							if (response.succeeded) {
								if (response.data === null) {
									setAnalysis(`0 spend below ${paymentTerms} days`);
									setIsLoading(false);
								} else {
									if (response.data.length > 0) {
										const sum = response.data.reduce((acc, obj) => {
											return acc + obj.totalSpend;
										}, 0);
										const analysisString = `${formatSpendWithDecimalAndSymbol(
											sum,
											currencySymbol,
											hideDecimals,
											2
										).toUpperCase()} ${daysText} ${paymentTerms} days`;
										setAnalysis(analysisString);
										const rowResult = response.data.map(el => {
											return {
												...el,
												category: el.category === null ? 'NA' : el.category,
												totalSpend: formatSpendWithDecimalAndSymbol(
													el.totalSpend,
													currencySymbol,
													hideDecimals,
													2
												).toUpperCase(),
											};
										});
										setGridData(prevState => ({
											...prevState,
											rowData: rowResult,
											columnDefs: columnDefs,
											defaultColDef: defaultColDefination,
										}));
										setIsLoading(false);
									}
								}
							} else {
								setAnalysis(`0 spend below ${paymentTerms} days`);
								setGridData(initialGridData);
								setIsLoading(false);
							}
						})
						.catch(() => setIsLoading(false));
				} else {
					setIsLoading(false);
				}
			}
		}, // eslint-disable-next-line
		[
			isSaving,
			dateRangeData,
			categoryName,
			isSettingsFetching,
			isFetching,
			isDirect,
			isSourceable,
			taxonomyType,
			customFilters,
			customFiltersValues,
		]
	);

	return (
		<PaymentTermsContext.Provider value={{ isLoading, analysis, gridData }}>
			{children}
		</PaymentTermsContext.Provider>
	);
};

export const usePaymentTermsContext = () => {
	return useContext(PaymentTermsContext);
};
